/**
 * Agrega linhas mensais (grão conta+mês) em totais por conta contábil.
 */

/**
 * Sentinela de transporte para lançamentos sem vínculo contábil (TXM-325 item 03).
 * Persistência/API/URL de drill-down: `__sem_conta__`. Exibição: ver labelConta().
 */
export const SEM_CONTA_SENTINEL = "__sem_conta__";

/**
 * Rótulo de exibição de conta contábil (tabela, multiselect, cabeçalho do drill-down).
 * Mapeia o sentinela de transporte para o rótulo amigável "Sem Conta Contábil".
 */
export function labelConta(ecdContaContabil, contaContabil) {
  const cod = String(ecdContaContabil || "").trim();
  if (cod === SEM_CONTA_SENTINEL) return "Sem Conta Contábil";
  const nome = String(contaContabil || "").trim();
  if (cod && nome) return `${cod} — ${nome}`;
  return cod || nome || "—";
}

function minCompetencia(a, b) {
  if (!a) return b || "";
  if (!b) return a;
  return a < b ? a : b;
}

function maxCompetencia(a, b) {
  if (!a) return b || "";
  if (!b) return a;
  return a > b ? a : b;
}

/**
 * Chave de agrupamento: código + descrição. TXM-325 — a mesma conta pode aparecer com
 * descrições diferentes (renomeação de plano de contas entre exercícios, ou variantes de
 * encoding em `sped_lista_de_contas`); o usuário precisa distinguir cada uma na tela, não
 * ver uma descrição "vencer" silenciosamente sobre a outra.
 */
function chaveAgregacao(linha) {
  const cod = String(linha.ecdContaContabil || "").trim();
  if (!cod) return "__sem_conta__";
  const nome = String(linha.contaContabil || "").trim();
  return `${cod} ${nome}`;
}

/**
 * @param {Array<object>} linhas — já filtradas
 * @returns {Array<object>} contas agregadas
 */
export function agregarPorConta(linhas) {
  const lista = Array.isArray(linhas) ? linhas : [];
  const porConta = new Map();

  for (const linha of lista) {
    const chave = chaveAgregacao(linha);
    const atual = porConta.get(chave) || {
      ecdContaContabil: linha.ecdContaContabil || "",
      contaContabil: linha.contaContabil || "",
      qtdLancamentos: 0,
      base: 0,
      baseSelic: 0,
      pis: 0,
      cofins: 0,
      taxaSelicMin: null,
      taxaSelicMax: null,
      competenciaMin: "",
      competenciaMax: "",
      compoeProjeto: false,
      baseLegalId: null,
      baseLegalDescricao: null,
      contaResultado: false,
      projetos: new Set(),
    };

    atual.contaContabil = atual.contaContabil || linha.contaContabil || "";
    atual.qtdLancamentos += Number(linha.qtdLancamentos) || 0;
    const base = Number(linha.base) || 0;
    atual.base += base;
    atual.baseSelic += Number(linha.baseSelic) || 0;
    atual.pis += Number(linha.pis) || 0;
    atual.cofins += Number(linha.cofins) || 0;
    if (linha.taxaSelicPct != null) {
      const taxa = Number(linha.taxaSelicPct);
      if (Number.isFinite(taxa)) {
        atual.taxaSelicMin = atual.taxaSelicMin == null ? taxa : Math.min(atual.taxaSelicMin, taxa);
        atual.taxaSelicMax = atual.taxaSelicMax == null ? taxa : Math.max(atual.taxaSelicMax, taxa);
      }
    }
    atual.competenciaMin = minCompetencia(atual.competenciaMin, linha.competencia);
    atual.competenciaMax = maxCompetencia(atual.competenciaMax, linha.competencia);
    atual.qtdMeses = (atual.qtdMeses || 0) + 1;
    if (linha.compoeProjeto) atual.compoeProjeto = true;
    // O grão da seleção é a conta, então todas as competências carregam a mesma base
    // legal — a primeira não nula define o agregado.
    if (atual.baseLegalId == null && linha.baseLegalId != null) {
      atual.baseLegalId = linha.baseLegalId;
      atual.baseLegalDescricao = linha.baseLegalDescricao ?? null;
    }
    if (linha.contaResultado) atual.contaResultado = true;
    if (linha.projetoCodigo) atual.projetos.add(linha.projetoCodigo);

    porConta.set(chave, atual);
  }

  return Array.from(porConta.values()).map((item) => ({
    ecdContaContabil: item.ecdContaContabil,
    contaContabil: item.contaContabil,
    qtdLancamentos: item.qtdLancamentos,
    base: item.base,
    taxaSelicMin: item.taxaSelicMin,
    taxaSelicMax: item.taxaSelicMax,
    baseSelic: item.baseSelic,
    pis: item.pis,
    cofins: item.cofins,
    competenciaMin: item.competenciaMin,
    competenciaMax: item.competenciaMax,
    qtdMeses: item.qtdMeses || 0,
    compoeProjeto: item.compoeProjeto,
    baseLegalId: item.baseLegalId,
    baseLegalDescricao: item.baseLegalDescricao,
    contaResultado: Boolean(item.contaResultado),
    // TXM-558 — projeto(s) aprovado(s) que utilizaram esta conta; só vem no modo consulta.
    projetoCodigo: item.projetos.size ? Array.from(item.projetos).sort().join(", ") : null,
  }));
}

/**
 * Totais do dashboard a partir das linhas filtradas (grão mês).
 * @param {Array<object>} linhas
 */
export function calcularTotaisDashboard(linhas) {
  const lista = Array.isArray(linhas) ? linhas : [];
  return lista.reduce(
    (acc, linha) => {
      acc.baseSelic += Number(linha.baseSelic) || 0;
      acc.pis += Number(linha.pis) || 0;
      acc.cofins += Number(linha.cofins) || 0;
      return acc;
    },
    { baseSelic: 0, pis: 0, cofins: 0 }
  );
}

/**
 * TXM-325 item 07 — Detalhes habilitado quando sumarizacao_status=sucesso E status ∈ {sucesso, divergencia}.
 * @param {object} row
 * @returns {boolean}
 */
export function podeAbrirDetalheSumarizacao(row) {
  return (
    row?.sumarizacaoStatus === "sucesso" &&
    (row?.status === "sucesso" || row?.status === "divergencia")
  );
}

/**
 * @param {object} row
 * @returns {boolean}
 */
export function podeProcessarSumarizacao(row) {
  const sum = row?.sumarizacaoStatus;
  const ap = row?.status;
  if (sum === "processando" || sum === "sucesso") return false;
  return ap === "sucesso" || ap === "erro";
}

/**
 * @param {object} row
 * @param {Record<string, string>} hints
 * @returns {string|null}
 */
export function hintDetalheSumarizacao(row, hints = {}) {
  if (podeAbrirDetalheSumarizacao(row)) return null;
  const sum = row?.sumarizacaoStatus;
  const ap = row?.status;
  if (sum === "pendente" && (ap === "sucesso" || ap === "erro")) {
    return "Sumarização pendente — use Processar";
  }
  return hints[sum] || "Sumarização indisponível";
}

export function formatarMoeda(valor) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return "—";
  return `R$ ${n.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatarPercentual(valor) {
  if (valor == null) return "—";
  const n = Number(valor);
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

/**
 * Selic (% acum.) — 3 a 4 casas decimais (padrão do protótipo).
 */
export function formatarSelicPctAcum(valor) {
  if (valor == null) return "—";
  const n = Number(valor);
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 4 })}%`;
}

export function competenciaParaMesAno(iso) {
  if (!iso || iso.length < 7) return "—";
  const [y, m] = iso.slice(0, 10).split("-");
  return `${m}/${y}`;
}
